import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  RefreshCw, 
  Download, 
  Clock, 
  Wallet,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TurnstileModal } from "@/components/auth/TurnstileModal";
import { isVerified, getVerificationToken, storeVerification, isExpiringSoon } from "@/lib/turnstile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UpgradeModal } from "@/components/billing/UpgradeModal";
import { useSearchParams } from "react-router-dom";
import { TimeRangeSelector } from "@/components/core/TimeRangeSelector";

interface DashboardHeaderProps {
  address?: string;
  lastSynced?: Date | null;
  onRefresh?: (captchaToken?: string) => void;
  onExport?: (type: 'fills' | 'daily' | 'portfolio') => void;
  isRefreshing?: boolean;
  isPro?: boolean;
  subscription?: { status: string | null; current_period_end: string | null } | null;
  title?: string;
  rangeDays: number;
  onRangeDaysChange: (days: number) => void;
  onRefreshSubscription?: () => void;
  isRefreshingSubscription?: boolean;
}

export const DashboardHeader = ({
  address = "0x1234...5678",
  lastSynced,
  onRefresh,
  onExport,
  isRefreshing = false,
  isPro = false,
  subscription,
  title = "Dashboard",
  rangeDays,
  onRangeDaysChange,
  onRefreshSubscription,
  isRefreshingSubscription = false,
}: DashboardHeaderProps) => {
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const isDevelopment = import.meta.env.DEV;
  const [turnstileModalOpen, setTurnstileModalOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('upgrade') === '1') {
      setUpgradeModalOpen(true);
    }
  }, [searchParams]);

  // Check verification status and handle expiry
  useEffect(() => {
    if (!isDevelopment && !isVerified()) {
      // Show modal if not verified
      setTurnstileModalOpen(true);
    } else if (!isDevelopment && isExpiringSoon()) {
      // Show modal if verification is about to expire
      setTurnstileModalOpen(true);
    }
  }, []);

  // Periodic check for verification expiry (every 2 minutes)
  useEffect(() => {
    if (isDevelopment) return;

    const interval = setInterval(() => {
      if (!isVerified()) {
        setTurnstileModalOpen(true);
      }
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, [isDevelopment]);

  // Update captcha token from storage
  useEffect(() => {
    if (!isDevelopment) {
      const token = getVerificationToken();
      if (token) {
        setCaptchaToken(token);
      }
    }
  }, []);
  
  const formatAddress = (addr: string) => {
    if (addr.length <= 12) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatLastSynced = (date?: Date) => {
    if (!date) return "Never";
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
      {/* Left side - Title and wallet */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
            <BarChart3 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <div className="flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" />
                <span className="font-mono">{formatAddress(address)}</span>
              </div>
              <span className="text-border">•</span>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{formatLastSynced(lastSynced)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Pro badge - show when Pro */}
        {isPro && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-amber-500">Pro</span>
            </div>
            {subscription?.current_period_end && (
              <>
                <span className="text-border">•</span>
                <span className="text-xs text-muted-foreground">
                  Until {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              </>
            )}
          </div>
        )}

        {/* Upgrade button - show when not Pro */}
        {!isPro && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setUpgradeModalOpen(true)}
            className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            Upgrade to Pro
          </Button>
        )}

        {/* Time range selector */}
        <TimeRangeSelector rangeDays={rangeDays} onRangeDaysChange={onRangeDaysChange} />

        {/* Refresh button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (!isDevelopment && !isVerified()) {
              setTurnstileModalOpen(true);
              return;
            }
            
            const token = isDevelopment ? 'dev-token' : getVerificationToken();
            onRefresh?.(token);
          }}
          disabled={isRefreshing || (!isDevelopment && !isVerified())}
          className="border-border/50"
        >
          <RefreshCw className={cn("w-4 h-4 mr-1.5", isRefreshing && "animate-spin")} />
          {isRefreshing ? 'Syncing...' : isVerified() ? 'Sync' : 'Verify'}
        </Button>

        {/* Export button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="bg-muted/50 hover:bg-muted">
              <Download className="w-4 h-4 mr-1.5" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover/95 backdrop-blur-sm border-border/50">
            <DropdownMenuItem onClick={() => onExport?.('fills')}>
              Export Fills (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.('daily')}>
              Export Daily Stats (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.('portfolio')}>
              Export Portfolio (CSV)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>


      <TurnstileModal
        isOpen={turnstileModalOpen}
        onClose={() => setTurnstileModalOpen(false)}
        onSuccess={(token) => {
          storeVerification(token);
          setCaptchaToken(token);
        }}
        siteKey={siteKey}
        isDevelopment={isDevelopment}
      />

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={(open) => {
          setUpgradeModalOpen(open);
          if (!open && searchParams.get('upgrade') === '1') {
            const next = new URLSearchParams(searchParams);
            next.delete('upgrade');
            setSearchParams(next, { replace: true });
          }
        }}
        onRefreshStatus={() => onRefreshSubscription?.()}
        isRefreshing={isRefreshingSubscription}
      />
    </div>
  );
};
